/**
 * The main survey dialog component
 */
import React, { Fragment } from "react";
import { CloseButton, Form, Modal } from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import SurveyDialogButtons from "./SurveyDialogButtons";
import PropTypes from "prop-types";

const surveyStyle = {
  maxHeight: "calc(100vh - 5em)",
  overflow: "auto",
  position: "fixed",
  bottom: 0,
  right: "1rem",
  minWidth: "32em",
  zIndex: 9999999,
};

const SurveyDialog = (props) => {
  const {
    surveyTitle,
    surveyId,
    bluePrintStyle,
    disableCloseButton,
    isOpen,
    showSurveyCompletion,
    handleOnClick,
    questions,
    questionIndex,
    handleSubmit,
    selectedAnswer,
    handleOnChange,
    isValid,
    handleBackClick,
    isEndOfSurvey,
    handleNextClick,
    isIdleTimeoutEnabled,
    isIdle,
  } = props;

  const titleId = surveyTitle.replaceAll(" ", "-");

  const computeRenderLogicForIdleTimeout = () => {
    //formatted weirdly to see the case better. essentially the idle timeout enabled will determine if
    // we need to wait for isIdle to show. otherwise if idle timeout is disabled, then we don't care about
    // isIdle
    return (
      (isOpen && isIdleTimeoutEnabled && isIdle && !showSurveyCompletion) ||
      (isOpen && !isIdleTimeoutEnabled && !showSurveyCompletion)
    );
  };

  return (
    <FocusTrap
      active={
        (isOpen && isIdleTimeoutEnabled && isIdle && !showSurveyCompletion) ||
        (isOpen && !isIdleTimeoutEnabled && !showSurveyCompletion)
      }
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        initialFocus: `#surveyModal\\ ${titleId}\\ ${surveyId} > div > div.modal-header > button`,
      }}
    >
      <aside aria-label={`surveyModal-${titleId}-aside`} role={"complementary"}>
        {computeRenderLogicForIdleTimeout() && (
          <Modal.Dialog
            style={surveyStyle}
            role={"dialog"}
            id={`surveyModal ${titleId} ${surveyId}`}
            aria-label={`surveyModal-${titleId}-modal-dialog`}
            className={`mobile-survey balto-${bluePrintStyle}`}
          >
            <Modal.Header id={"surveyModalHeader"} onHide={handleOnClick}>
              <Modal.Title as="h3" id={"surveyModalTitle"}>
                {questions[questionIndex] &&
                  questions[questionIndex].questionTitle}
              </Modal.Title>
              <CloseButton
                data-dismiss="modal"
                aria-label="Close"
                id="baltoCloseButton"
                className={disableCloseButton}
                onClick={handleOnClick}
              />
            </Modal.Header>
            <Modal.Body id={"surveyModalBody"}>
              {questions[questionIndex] &&
                questions[questionIndex].questionSubText && (
                  <Fragment>
                    <p className={"p"}>
                      {questions[questionIndex].questionSubText}
                    </p>
                  </Fragment>
                )}
              <Form onSubmit={handleSubmit}>
                {questions[questionIndex] &&
                  !questions[questionIndex].isFreeText &&
                  Array.isArray(questions[questionIndex].questions) &&
                  questions[questionIndex].questions.length > 0 &&
                  questions[questionIndex].questions.map((question, index) => (
                    <Fragment key={"surveyQuestion" + index}>
                      <Form.Group>
                        <Form.Check
                          type="radio"
                          label={question}
                          name={questions[questionIndex].questionTitle}
                          id={question}
                          value={question}
                          checked={selectedAnswer === question}
                          onChange={handleOnChange}
                        />
                      </Form.Group>
                    </Fragment>
                  ))}
                {questions[questionIndex] &&
                  questions[questionIndex].isFreeText && (
                    <Fragment>
                      <Form.Group>
                        <Form.Control
                          as={"textarea"}
                          rows={3}
                          onChange={handleOnChange}
                          isInvalid={!isValid}
                          id={"surveyTextArea"}
                          title="Survey Text Area"
                          autoFocus={true}
                        />
                      </Form.Group>
                    </Fragment>
                  )}
                <SurveyDialogButtons
                  questions={questions}
                  questionIndex={questionIndex}
                  handleBackClick={handleBackClick}
                  isEndOfSurvey={isEndOfSurvey}
                  handleNextClick={handleNextClick}
                  isValid={isValid}
                />
              </Form>
            </Modal.Body>
          </Modal.Dialog>
        )}
      </aside>
    </FocusTrap>
  );
};

SurveyDialog.propTypes = {
  surveyTitle: PropTypes.string.isRequired,
  surveyId: PropTypes.string.isRequired,
  bluePrintStyle: PropTypes.string,
  disableCloseButton: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  showSurveyCompletion: PropTypes.bool.isRequired,
  handleOnClick: PropTypes.func.isRequired,
  questions: PropTypes.array,
  questionIndex: PropTypes.number,
  handleSubmit: PropTypes.func.isRequired,
  selectedAnswer: PropTypes.any,
  handleOnChange: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  handleBackClick: PropTypes.func.isRequired,
  isEndOfSurvey: PropTypes.bool.isRequired,
  handleNextClick: PropTypes.func.isRequired,
  isIdleTimeoutEnabled: PropTypes.bool,
  isIdle: PropTypes.bool.isRequired,
};

export default SurveyDialog;
